/* eslint-disable max-lines */
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Category } from './category.js';
import { Institution } from './institution.js';
import { TextMultipleLangItem } from './TextMultipleLangItem.js';
import { Workshop } from './workshop.js';

@Entity()
export class WorkshopSet extends MongoEntity {
  static override _class = 'WorkshopSet';
  static override _collectionName = 'workshop-sets';

  @Field(type => String)
  name!: string;

  @validate(nonOptional())
  @Field(type => String)
  institutionID!: string;

  @DataLoadedField(type => Institution, ({ institutionID }) => institutionID)
  institution: Maybe<Institution>;

  @validate(nonOptional())
  @Field(type => String)
  categoryID!: string;

  @DataLoadedField(type => Category, ({ categoryID }) => categoryID)
  category: Maybe<Category>;

  @Field(type => String)
  timeframeID!: string;

  @DataLoadedField(type => Timeframe, ({ timeframeID }) => timeframeID)
  timeframe: Maybe<Timeframe>;

  @validate(async options => {
    const descriptions = (options.value ?? []) as TextMultipleLangItem[];

    if (!descriptions.length)
      throw new ValidationError('Beschreibung ist ein Pflichtfeld', {
        translationKey: 'validation_non_optional',
        key: options.key,
      });
  })
  @Field(type => [TextMultipleLangItem])
  translatedDescriptionHTML!: TextMultipleLangItem[];

  @Field(type => [Workshop], {
    resolve: function (this: Partial<WorkshopSet>) {
      return async (gqlFields: BaseQLSelectionSet<Workshop>) => {
        // TODO: make it work: if (Array.isArray(this.workshops)) return this.workshops; // do not refetch
        const workshopCollection = await getCollection(Workshop._class);
        const institutionID = this.institutionID;
        if (!institutionID) throw new Error('Institution must exist to fetch workshops');
        const workshopQuery = workshopCollection.find({
          institutionID,
          'affiliation.affiliation': this._class,
          'affiliation.affiliationID': this._id,
        });

        return workshopQuery.toArray() as Promise<Workshop[]>;
      };
    },
  })
  workshops!: Workshop[];

  @Field(type => Boolean, {
    resolve: function (this: Partial<WorkshopSet>) {
      return async () => {
        // TODO: check own timeframe and weather all workshops are active
        return true;
      };
    },
  })
  isActive!: boolean;
}
